/**
 * ItemAlteration (Foundry: rule-element/item-alteration/rule-element.ts): change a property of
 * the Entries of a type (or one Entry by id) the Character holds, when the predicate passes for
 * that Entry. The change lands on the engine's copy and is listed on the altered Entry.
 */
import { applyAlteration, ITEM_ALTERATION_PROPERTIES, type ItemAlteration } from "../alterations";
import type { EngineItem } from "../items";
import { testPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties } from "../resolve";
import type { EngineState } from "../state";
import { PHYSICAL_ITEM_TYPES } from "../values";
import { AE_LIKE_MODES, CHANGE_MODE_DEFAULT_PRIORITIES, type AELikeMode } from "./ae-like";
import type { RuleHandler, RuleInstance } from "./base";

interface ItemAlterationData {
  itemId: string | null;
  itemType: string | null;
  mode: AELikeMode;
  property: string;
  value: unknown;
  fromEquipment: boolean;
  battleForm: boolean;
}

/** Properties processed at the end of data preparation. */
const DELAYED_PROPERTIES = ["pd-recovery-dc"];
const ABP_PROPERTIES = ["runes-potency", "runes-resilient", "runes-striking"];

function parse(rule: RuleInstance): void {
  const s = rule.source;
  if (!(AE_LIKE_MODES as readonly string[]).includes(String(s.mode))) return rule.fail(`mode: "${String(s.mode)}" is not a change mode`);
  if (typeof s.property !== "string" || !(ITEM_ALTERATION_PROPERTIES as readonly string[]).includes(s.property)) return rule.fail(`property: "${String(s.property)}" is not an alterable property`);
  const itemId = typeof s.itemId === "string" && s.itemId ? s.itemId : null;
  const itemType = typeof s.itemType === "string" && s.itemType ? s.itemType : null;
  if (!itemId && !itemType) return rule.fail("one of itemId and itemType must be defined");
  const value = s.value === undefined ? null : cleanRuleValue(s.value, true);
  const physicalParent = (PHYSICAL_ITEM_TYPES as readonly string[]).includes(rule.item.type);
  const fromEquipment = s.fromEquipment === false ? false : physicalParent || ABP_PROPERTIES.includes(s.property);
  if (typeof s.priority !== "number") rule.priority = (CHANGE_MODE_DEFAULT_PRIORITIES[s.mode as AELikeMode] ?? 50) + 100;
  rule.data = { itemId, itemType, mode: s.mode as AELikeMode, property: s.property, value: value ?? null, fromEquipment, battleForm: s.battleForm === true } satisfies ItemAlterationData as unknown as Record<string, unknown>;
}

function itemsOfType(rule: RuleInstance, state: EngineState, data: ItemAlterationData): EngineItem[] {
  if (data.itemId) {
    const itemId = resolveInjectedProperties(rule, state, data.itemId, { warn: false });
    return state.items.filter((i) => i.id === itemId);
  }
  if (data.itemType) return state.items.filter((i) => i.type === data.itemType);
  return [];
}

/** Apply the alteration to the matching items, or to the one given (a weapon that arrived late). */
export function applyItemAlteration(rule: RuleInstance, state: EngineState, singleItem: EngineItem | null = null): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as ItemAlterationData;
  const itemId = data.itemId ? resolveInjectedProperties(rule, state, data.itemId, { warn: false }) : null;
  const items = singleItem ? (singleItem.id === itemId || singleItem.type === data.itemType ? [singleItem] : []) : itemsOfType(rule, state, data);
  if (items.length === 0) return;
  const predicate = resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false });
  if (data.battleForm && !predicate.includes("battle-form")) predicate.push("battle-form");
  const actorOptions = predicate.length > 0 ? [...state.rollOptions.get()] : [];
  const parentOptions = predicate.length > 0 ? rule.item.getRollOptions("parent") : [];
  const alteration: ItemAlteration = { mode: data.mode, property: data.property, value: data.value, fromEquipment: data.fromEquipment };
  for (const item of items) {
    const itemOptions = predicate.length > 0 ? item.getRollOptions("item") : [];
    if (predicate.length > 0 && !testPredicate(predicate, new Set([...actorOptions, ...parentOptions, ...itemOptions]))) continue;
    const problem = applyAlteration(rule, state, item, alteration);
    if (problem) rule.problems.push(problem);
  }
}

/** Apply every registered ItemAlteration to an item that arrives after the applyAEs phase (Foundry: performLatePreparation). */
export function applyItemAlterationsTo(state: EngineState, item: EngineItem): void {
  for (const rule of state.synthetics.itemAlterations) applyItemAlteration(rule, state, item);
}

function onApplyActiveEffects(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as ItemAlterationData;
  state.synthetics.itemAlterations.push(rule);
  if (!DELAYED_PROPERTIES.includes(data.property)) applyItemAlteration(rule, state);
}

function afterPrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as ItemAlterationData;
  if (DELAYED_PROPERTIES.includes(data.property)) applyItemAlteration(rule, state);
}

export const ItemAlterationHandler: RuleHandler = { key: "ItemAlteration", parse, onApplyActiveEffects, afterPrepareData };
